import {
  PROVIDER_SEND_TURN_MAX_ATTACHMENTS,
  PROVIDER_SEND_TURN_MAX_FILE_BYTES,
} from "@cmmd-forge/contracts";

import type { ComposerFileAttachment, ComposerImageAttachment } from "../../composerDraftStore";
import { composerFileAttachmentStore } from "../../composerFileAttachmentStore";
import { releaseUnreferencedComposerFile } from "../../composerFileAttachmentLifecycle";
import { randomUUID } from "../../lib/utils";
import { evaluateComposerImageAdmission } from "./ChatComposer.logic";

const fileSizeLimitLabel = `${Math.round(PROVIDER_SEND_TURN_MAX_FILE_BYTES / (1024 * 1024))}MB`;

export function admitComposerAttachmentBatch(input: {
  readonly files: ReadonlyArray<File>;
  readonly currentAttachmentCount: number;
  readonly fileOnly: boolean;
}): {
  readonly images: ComposerImageAttachment[];
  readonly files: File[];
  readonly error: string | null;
} {
  const images: ComposerImageAttachment[] = [];
  const acceptedFiles: File[] = [];
  let attachmentCount = input.currentAttachmentCount;
  let error: string | null = null;
  for (const file of input.files) {
    if (!input.fileOnly && file.type.startsWith("image/")) {
      const admission = evaluateComposerImageAdmission({
        file,
        currentImageCount: attachmentCount,
      });
      if (!admission.accepted) {
        error = admission.error;
        if (admission.stop) break;
        continue;
      }
      images.push({
        type: "image",
        id: randomUUID(),
        name: admission.fallbackName,
        mimeType: admission.file.type,
        sizeBytes: admission.file.size,
        previewUrl: URL.createObjectURL(admission.file),
        file: admission.file,
      });
      attachmentCount += 1;
      continue;
    }
    if (attachmentCount >= PROVIDER_SEND_TURN_MAX_ATTACHMENTS) {
      error = `You can attach up to ${PROVIDER_SEND_TURN_MAX_ATTACHMENTS} files per message.`;
      break;
    }
    if (file.size > PROVIDER_SEND_TURN_MAX_FILE_BYTES) {
      error = `'${file.name}' exceeds the ${fileSizeLimitLabel} attachment limit.`;
      continue;
    }
    acceptedFiles.push(file);
    attachmentCount += 1;
  }
  return { images, files: acceptedFiles, error };
}

export function shouldSkipComposerAttachmentIntake(input: {
  readonly disabled: boolean;
  readonly fileCount: number;
  readonly blockedReason: string | null | undefined;
  readonly onBlocked: ((message: string) => void) | undefined;
}): boolean {
  if (input.disabled || input.fileCount === 0) return true;
  if (!input.blockedReason) return false;
  input.onBlocked?.(input.blockedReason);
  return true;
}

export function publishAdmittedComposerImages(input: {
  readonly currentImages: ReadonlyArray<ComposerImageAttachment>;
  readonly images: ReadonlyArray<ComposerImageAttachment>;
  readonly publish: (images: ComposerImageAttachment[]) => void;
}): ComposerImageAttachment[] {
  const nextImages = [...input.currentImages, ...input.images];
  if (input.images.length > 0) input.publish([...input.images]);
  return nextImages;
}

async function persistComposerFileBatch(
  files: ReadonlyArray<File>,
): Promise<ComposerFileAttachment[]> {
  const attachments: ComposerFileAttachment[] = [];
  try {
    for (const file of files) {
      const ref = await composerFileAttachmentStore.put(file);
      attachments.push({ ...ref, type: "file", file });
    }
    return attachments;
  } catch (error) {
    deletePersistedComposerFiles(attachments);
    throw error;
  }
}

export async function stageComposerFileBatch(input: {
  readonly files: ReadonlyArray<File>;
  readonly scopeIsActive: () => boolean;
  readonly currentFiles: () => ReadonlyArray<ComposerFileAttachment>;
  readonly publish: (files: ComposerFileAttachment[]) => void;
}): Promise<{ readonly error: string | null; readonly discarded: boolean }> {
  const attachments: ComposerFileAttachment[] = [];
  try {
    attachments.push(...(await persistComposerFileBatch(input.files)));
    if (!input.scopeIsActive()) {
      deletePersistedComposerFiles(attachments);
      return { error: null, discarded: true };
    }
    input.publish([...input.currentFiles(), ...attachments]);
    return { error: null, discarded: false };
  } catch {
    deletePersistedComposerFiles(attachments);
    return {
      error: `Could not save '${input.files[0]?.name ?? "file"}' for this draft.`,
      discarded: !input.scopeIsActive(),
    };
  }
}

function deletePersistedComposerFiles(files: ReadonlyArray<ComposerFileAttachment>): void {
  for (const file of files) void releaseUnreferencedComposerFile(file.id);
}

function revokeComposerImagePreviews(images: ReadonlyArray<ComposerImageAttachment>): void {
  for (const image of images) URL.revokeObjectURL(image.previewUrl);
}

export function revokeDiscardedComposerImagePreviews(input: {
  readonly discarded: boolean;
  readonly images: ReadonlyArray<ComposerImageAttachment>;
}): void {
  if (input.discarded) revokeComposerImagePreviews(input.images);
}

export function reportComposerAttachmentIntakeResult(input: {
  readonly discarded: boolean;
  readonly error: string | null;
  readonly onError: (message: string | null) => void;
}): void {
  if (!input.discarded) input.onError(input.error);
}
